/** SPEC §20: planning per werkpakket, speling en kritiek pad. */
import { addDagen, weekdagIndex } from './datum';
import type { ISODate } from './types';

/** Werkdagen (ma–vr) in (van, tot]; negatief als tot vóór van ligt. */
export function werkdagenTussen(van: ISODate, tot: ISODate): number {
  if (tot === van) return 0;
  const teken = tot > van ? 1 : -1;
  const [a, b] = teken > 0 ? [van, tot] : [tot, van];
  let n = 0;
  for (let d = addDagen(a, 1); d <= b; d = addDagen(d, 1)) if (weekdagIndex(d) <= 4) n++;
  return n * teken;
}

export interface WpPlanning {
  id: string;
  naam?: string;
  start: ISODate | null;
  eind: ISODate | null;
  afhankelijkVanId?: string | null;
}

/** Eerste start en laatste eind van de ingeplande taken per werkpakket (zoals de Gantt-tabel). */
export function werkpakketPlanning(
  wps: { id: string; naam: string; afhankelijkVanId?: string | null }[],
  tps: { id: string; werkpakketId?: string | null }[],
  taken: { werkpakketId?: string | null; takenpakketId?: string | null; startdatum?: string | null; einddatum?: string | null }[],
): WpPlanning[] {
  return wps.map((w) => {
    const tpIds = new Set(tps.filter((t) => t.werkpakketId === w.id).map((t) => t.id));
    const tk = taken.filter((t) => t.startdatum && (t.werkpakketId === w.id || (t.takenpakketId && tpIds.has(t.takenpakketId))));
    const s = tk.map((t) => t.startdatum!).sort();
    const e = tk.map((t) => t.einddatum ?? t.startdatum!).sort();
    return { id: w.id, naam: w.naam, start: s[0] ?? null, eind: e.at(-1) ?? null, afhankelijkVanId: w.afhankelijkVanId ?? null };
  });
}

export interface Speling {
  /** werkdagen speling; 0 = kritiek */
  speling: number;
  kritiek: boolean;
}

/**
 * Speling per werkpakket (werkdagen): hoeveel later het klaar kan zijn zonder dat een opvolger
 * (afhankelijkVanId) of het einde van het project opschuift. Alleen werkpakketten met start en eind.
 */
export function speling(items: WpPlanning[]): { perId: Map<string, Speling>; einde: ISODate | null } {
  const gepland = items.filter((i) => i.start && i.eind);
  const perId = new Map<string, Speling>();
  if (!gepland.length) return { perId, einde: null };
  const einde = gepland.map((i) => i.eind!).sort().at(-1)!;
  const opvolgers = new Map<string, WpPlanning[]>();
  for (const i of gepland) if (i.afhankelijkVanId) opvolgers.set(i.afhankelijkVanId, [...(opvolgers.get(i.afhankelijkVanId) ?? []), i]);
  const memo = new Map<string, number>();
  const bezig = new Set<string>();
  const tf = (i: WpPlanning): number => {
    if (memo.has(i.id)) return memo.get(i.id)!;
    if (bezig.has(i.id)) return 0; // kring in afhankelijkheden
    bezig.add(i.id);
    const ops = opvolgers.get(i.id) ?? [];
    // werkdagen strikt tussen het eind van dit pakket en de start van de opvolger
    const tussen = (o: WpPlanning) => werkdagenTussen(i.eind!, o.start!) - (weekdagIndex(o.start!) <= 4 ? 1 : 0);
    const naarEinde = werkdagenTussen(i.eind!, einde);
    const w = Math.max(0, Math.min(naarEinde, ...ops.map((o) => tussen(o) + tf(o))));
    bezig.delete(i.id);
    memo.set(i.id, w);
    return w;
  };
  for (const i of gepland) {
    const w = tf(i);
    perId.set(i.id, { speling: w, kritiek: w === 0 });
  }
  return { perId, einde };
}

export interface BaselineData {
  werkpakketten: { id: string; naam: string; start: ISODate | null; eind: ISODate | null; deadline: ISODate | null }[];
  einde: ISODate | null;
  prognose: ISODate | null;
  livegang: ISODate | null;
}

/** Verschil in werkdagen (positief = later dan de baseline). */
export function baselineVerschil(baseline: ISODate | null | undefined, nu: ISODate | null | undefined): number | null {
  return baseline && nu ? werkdagenTussen(baseline, nu) : null;
}
